import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import {
  j212Api,
  FJ210,
  FJ211,
  FJ211Input,
  CreateJ212Request,
  UpdateJ212Request,
} from '../../../services/j212Api';
import { J212Mode } from './useJ212Mode';

// ---- Form data shape ----

export interface FJ211FormItem {
  fj211_02: number;    // 加工代號
  fj211_11: string;    // 項次
  fj211_12: string;    // (sub-code)
  fj211_13: number;    // 產品代號
  fj211_14: number;    // (col4)
  fj211_15: number;    // 預估成本(NT$)
  fj211_16: number;    // 費用率%
  fj211_17: string;    // 毛利率%
  fj211_18: string;    // 產品名稱
  fj211_19: string;    // 成品組織/規格
  fj211_20: string;    // 加工別
  fj211_21: number;    // 針孔內
  fj211_22: number;    // 針孔外
  fj211_23: number;    // 碼重
  fj211_24: number;    // 米平方重
  fj211_25: string;    // 建議售價(US$)
  fj211_261: string;   // 報告單號1
  fj211_262: string;   // 報告單號2
  fj211_263: string;   // 報告單號3
  fj211_27: boolean;   // 是否為暢銷品
}

export interface J212FormData {
  fj210_01: string;  // 專輯編號 (PK, read-only)
  fj210_11: string;  // 製作日期
  fj210_12: string;  // 專輯主題
  fj210_13: string;  // 製作人員
  fj210_14: number;  // 製作份數
  fj210_15: string;  // 公司名稱
  fj210_16: number;  // 預估匯率
  fj210_17: string;  // 交易條件
  fj210_18: number;  // 專輯類別
  fj210_20: number;  // 總項數
  fj210_21: string;  // 備註
  fj210_23: string;  // 專輯抬頭日期
  fj210_80: string;  // 狀態
  details: FJ211FormItem[];
}

const emptyDetail = (): FJ211FormItem => ({
  fj211_02: 0,
  fj211_11: '',
  fj211_12: '',
  fj211_13: 0,
  fj211_14: 0,
  fj211_15: 0,
  fj211_16: 0,
  fj211_17: '',
  fj211_18: '',
  fj211_19: '',
  fj211_20: '',
  fj211_21: 0,
  fj211_22: 0,
  fj211_23: 0,
  fj211_24: 0,
  fj211_25: '',
  fj211_261: '',
  fj211_262: '',
  fj211_263: '',
  fj211_27: false,
});

const emptyForm = (): J212FormData => ({
  fj210_01: '',
  fj210_11: dayjs().format('YYYY-MM-DD'),
  fj210_12: '',
  fj210_13: '',
  fj210_14: 0,
  fj210_15: '',
  fj210_16: 1,
  fj210_17: '',
  fj210_18: 0,
  fj210_20: 0,
  fj210_21: '',
  fj210_23: '',
  fj210_80: '',
  details: [],
});

function recordToForm(r: FJ210): J212FormData {
  return {
    fj210_01: r.fj210_01 || '',
    fj210_11: r.fj210_11 ? dayjs(r.fj210_11).format('YYYY-MM-DD') : '',
    fj210_12: r.fj210_12 || '',
    fj210_13: r.fj210_13 || '',
    fj210_14: r.fj210_14 ?? 0,
    fj210_15: r.fj210_15 || '',
    fj210_16: r.fj210_16 ?? 0,
    fj210_17: r.fj210_17 || '',
    fj210_18: r.fj210_18 ?? 0,
    fj210_20: r.fj210_20 ?? 0,
    fj210_21: r.fj210_21 || '',
    fj210_23: r.fj210_23 || '',
    fj210_80: r.fj210_80 || '',
    details: (r.details || []).map((d: FJ211) => ({
      fj211_02: d.fj211_02 ?? 0,
      fj211_11: d.fj211_11 || '',
      fj211_12: d.fj211_12 || '',
      fj211_13: d.fj211_13 ?? 0,
      fj211_14: d.fj211_14 ?? 0,
      fj211_15: d.fj211_15 ?? 0,
      fj211_16: d.fj211_16 ?? 0,
      fj211_17: d.fj211_17 || '',
      fj211_18: d.fj211_18 || '',
      fj211_19: d.fj211_19 || '',
      fj211_20: d.fj211_20 || '',
      fj211_21: d.fj211_21 ?? 0,
      fj211_22: d.fj211_22 ?? 0,
      fj211_23: d.fj211_23 ?? 0,
      fj211_24: d.fj211_24 ?? 0,
      fj211_25: d.fj211_25 || '',
      fj211_261: d.fj211_261 || '',
      fj211_262: d.fj211_262 || '',
      fj211_263: d.fj211_263 || '',
      fj211_27: d.fj211_27 ?? false,
    })),
  };
}

function formToRequest(form: J212FormData): CreateJ212Request {
  const details: FJ211Input[] = form.details.map(d => ({
    fj211_11: d.fj211_11,
    fj211_12: d.fj211_12,
    fj211_13: d.fj211_13,
    fj211_14: d.fj211_14,
    fj211_15: d.fj211_15,
    fj211_16: d.fj211_16,
    fj211_17: d.fj211_17,
    fj211_18: d.fj211_18,
    fj211_19: d.fj211_19,
    fj211_20: d.fj211_20,
    fj211_21: d.fj211_21,
    fj211_22: d.fj211_22,
    fj211_23: d.fj211_23,
    fj211_24: d.fj211_24,
    fj211_25: d.fj211_25,
    fj211_261: d.fj211_261,
    fj211_262: d.fj211_262,
    fj211_263: d.fj211_263,
    fj211_27: d.fj211_27,
  }));

  return {
    fj210_11: form.fj210_11 || null,
    fj210_12: form.fj210_12,
    fj210_13: form.fj210_13,
    fj210_14: form.fj210_14,
    fj210_15: form.fj210_15,
    fj210_16: form.fj210_16,
    fj210_17: form.fj210_17,
    fj210_18: form.fj210_18,
    fj210_21: form.fj210_21,
    fj210_23: form.fj210_23,
    fj210_80: form.fj210_80,
    details,
  };
}

// ---- Hook ----

export function useJ212Record(mode: J212Mode, enterBrowse: () => void) {
  const [records, setRecords] = useState<FJ210[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [formData, setFormData] = useState<J212FormData>(emptyForm());
  const [loading, setLoading] = useState(false);

  const current: FJ210 | null =
    currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;

  const loadRecords = useCallback(async (keyword?: string): Promise<FJ210[]> => {
    setLoading(true);
    try {
      const res = await j212Api.list(1, 500, keyword);
      const data = res.data || [];
      setRecords(data);
      return data;
    } catch {
      message.error('載入資料失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      setFormData(recordToForm(current));
    }
  }, [current, mode]);

  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  const navFirst = useCallback(() => {
    if (records.length > 0) setCurrentIdx(0);
  }, [records.length]);

  const navPrev = useCallback(() => {
    if (currentIdx > 0) setCurrentIdx(prev => prev - 1);
  }, [currentIdx]);

  const navNext = useCallback(() => {
    if (currentIdx < records.length - 1) setCurrentIdx(prev => prev + 1);
  }, [currentIdx, records.length]);

  const navLast = useCallback(() => {
    if (records.length > 0) setCurrentIdx(records.length - 1);
  }, [records.length]);

  const startAdd = useCallback(() => {
    setFormData(emptyForm());
  }, []);

  const startEdit = useCallback(() => {
    if (current) {
      setFormData(recordToForm(current));
    }
  }, [current]);

  const cancel = useCallback(() => {
    if (current) {
      setFormData(recordToForm(current));
    } else {
      setFormData(emptyForm());
    }
    enterBrowse();
  }, [current, enterBrowse]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      let savedId = '';
      const req = formToRequest(formData);
      if (mode === 'add') {
        const res = await j212Api.create(req as CreateJ212Request);
        savedId = res.fj210_01;
        message.success('新增成功');
      } else if (mode === 'edit' && current) {
        await j212Api.update(current.fj210_01, req as UpdateJ212Request);
        savedId = current.fj210_01;
        message.success('修改成功');
      }
      const data = await loadRecords();
      const idx = data.findIndex(r => r.fj210_01 === savedId);
      setCurrentIdx(idx >= 0 ? idx : 0);
      enterBrowse();
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, formData, current, loadRecords, enterBrowse]);

  const doDelete = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    try {
      await j212Api.delete(current.fj210_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
      enterBrowse();
    } catch (err: any) {
      const msg = err?.response?.data?.error || '刪除失敗';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [current, currentIdx, loadRecords, enterBrowse]);

  const quickSearch = useCallback(async (keyword: string) => {
    setLoading(true);
    try {
      const data = await loadRecords(keyword);
      if (data.length > 0) {
        setCurrentIdx(0);
      } else {
        setCurrentIdx(-1);
        message.info('查無資料');
      }
    } catch {
      message.error('查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [loadRecords]);

  const setField = useCallback(<K extends keyof J212FormData>(
    field: K,
    value: J212FormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    records,
    current,
    currentIdx,
    formData,
    loading,
    emptyDetail,
    setField,
    navFirst,
    navPrev,
    navNext,
    navLast,
    startAdd,
    startEdit,
    cancel,
    save,
    doDelete,
    quickSearch,
    loadRecords,
  };
}
